import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  SelectedAlertContainer,
  StyledTag,
  ChartSubTitleContainer,
  ChartSubTitleText,
  SelectedAlertContainerTitle
} from '../common/styled';
import AnalystLoadWidget from '../common/analystLoadWidget/AnalystLoadWidget';
import { Spin, Empty, Alert } from 'antd';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { fetchInvestigatorWorkload } from '../../../redux/actions/cm/investigators';
import {
  assignAlertsToUser,
  alertActionInfoModalClose,
  assignAlertsToUserRest
} from '../../../redux/actions/cm/alertAction';
import InfoModal from '../common/InfoModal';
import { investigatorListMapper, formatUserName } from '../utils/users';
import { InvestigatorItem, UserType } from '../propTypes';
import { getUniqArrayBy } from '../utils/common';
import { getCurrentUser } from '../../../redux/selectors/cm/users';

// FIN-13933 — a FRAML select-all can skip hundreds of cased alerts; name the first few (as the BE message does).
const SKIPPED_DEPT_SHOWN = 10;

const legend = [
  { fieldKey: 'cases', fieldName: 'Cases', color: '#F7B9B0' },
  { fieldKey: 'alerts', fieldName: 'Alerts', color: '#FCD697' }
];

export const AssignAlertModal = ({
  visible,
  handleExit,
  selectedIds,
  selectedAlerts,
  dispatch,
  investigators,
  showInfoModal,
  assignResult = null,
  isLoading,
  investigatorsAreLoading,
  currentUser,
  hasError,
  errorMessage,
  // FIN-11360: opt-in props so FRAML View can reuse this CM modal (BE behaviour +
  // API unchanged). Both default to current CM behaviour when unset.
  unassignedOnlyWarning = false,
  onAssignSuccess
}) => {
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);
  const [chartCurrentPage, setChartCurrentPage] = useState(1);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [items, setItems] = useState([]);
  const [totalChartItems, setTotalChartItems] = useState(0);
  const [selectedAlertCount, setSelectedAlertCount] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState('');

  useEffect(() => {
    if (visible && selectedIds.length) {
      setSelectedAlertIds(selectedIds);
      dispatch(fetchInvestigatorWorkload());
      setSelectedItemId(null);
    }
    dispatch(assignAlertsToUserRest());
  }, [selectedIds, visible]);

  useEffect(() => {
    const validAlerts = selectedAlerts.filter(({ alert }) =>
      selectedAlertIds.includes(alert.id)
    );
    const alertInvestigatorIds = getUniqArrayBy(validAlerts, [
      'metadata',
      'assignedUsernameId'
    ]);

    if (currentUser?.roles?.includes('INVESTIGATOR_PLUS')) {
      const currentUserData = investigatorListMapper(
        investigators.filter(({ user }) => user.id === currentUser.id)
      );

      setItems(currentUserData);
      setTotalChartItems(currentUserData.length);
    } else {
      const userData = investigatorListMapper(
        alertInvestigatorIds.length === 1
          ? investigators.filter(
              ({ user }) => user.id != alertInvestigatorIds[0]
            )
          : investigators,
        'alerts'
      );

      setItems(userData);
      setTotalChartItems(userData.length);
    }
  }, [investigators, selectedAlerts, selectedAlertIds]);

  const renderSelectedIds = () => {
    return selectedAlertIds.map((item) => (
      <StyledTag closable key={item} onClose={(e) => removeSelectedId(e, item)}>
        {item}
      </StyledTag>
    ));
  };

  const removeSelectedId = (e, removeId) => {
    e.preventDefault();
    if (selectedAlertIds.length > 1) {
      setSelectedAlertIds(
        selectedAlertIds.filter((id) => {
          return id != removeId;
        })
      );
      dispatch(fetchInvestigatorWorkload());
    }
  };

  const onChartPageChange = (page) => {
    setChartCurrentPage(page);
  };

  useEffect(() => {
    if (visible && selectedAlertIds.length <= 0) {
      handleExit();
    }
  }, [selectedAlertIds]);

  const onSelect = (userId) => {
    setSelectedItemId(userId);
  };

  const submitAssignModal = () => {
    if (selectedItemId) {
      const userName = formatUserName(
        investigators.find((i) => i['user']['id'] === selectedItemId)
      );
      setSelectedItemName(userName);
      const validAlertIds = selectedAlerts
        .filter(
          ({ alert, metadata }) =>
            metadata.assignedUsernameId !== selectedItemId &&
            selectedAlertIds.includes(alert.id)
        )
        .map(({ alert }) => alert.id);

      setSelectedAlertCount(validAlertIds.length);
      dispatch(
        assignAlertsToUser({
          alertIds: validAlertIds,
          user: selectedItemId
        })
      );
    }
  };

  // FIN-12893 — partial-assign summary from the BE response (null on the legacy all-or-nothing path).
  const notFoundIds = (assignResult && assignResult.notFound) || [];
  // FIN-13933 — alerts the BE skipped because the assignee is not in their case's department.
  const skippedDept = (assignResult && assignResult.skippedDepartment) || [];
  const isPartial = notFoundIds.length > 0 || skippedDept.length > 0;
  const assignedCount =
    assignResult && Array.isArray(assignResult.assigned)
      ? assignResult.assigned.length
      : selectedAlertCount;

  const infoModalButton = {
    primary: {
      label: 'OK',
      onClick: () => dispatch(alertActionInfoModalClose())
    }
  };

  useEffect(() => {
    if (showInfoModal) {
      handleExit();
      // FIN-11360: FRAML View clears its own selection + refetches its queue on
      // assign success (CM path passes no callback, so its behaviour is unchanged).
      if (onAssignSuccess) onAssignSuccess();
    }
  }, [showInfoModal]);

  const assignAlertsButtons = useMemo(
    () => ({
      primary: {
        label: 'Assign',
        onClick: submitAssignModal,
        isDisabled: selectedItemId ? false : true
      },
      secondary: {
        label: 'Cancel',
        onClick: handleExit
      }
    }),
    [selectedItemId]
  );

  return (
    <>
      <InfoModal isOpen={showInfoModal} buttons={infoModalButton}>
        {isPartial ? (
          // FIN-12893 — the BE assigns what resolves and reports the rest; say so instead of
          // claiming every selected alert was assigned (FRAML View: ids truncated to 63 chars
          // before FIN-13012 do not resolve).
          <>
            <p>
              <b>Alerts Partially Assigned</b>
            </p>
            {assignedCount} of {selectedAlertCount} selected alert
            {selectedAlertCount === 1 ? '' : 's'}{' '}
            {assignedCount === 1 ? 'has' : 'have'} been assigned to{' '}
            {selectedItemName}.
            {notFoundIds.length > 0 && (
              <p data-testid="assign-not-found">
                {notFoundIds.length} alert ID
                {notFoundIds.length === 1 ? '' : 's'} could not be found and{' '}
                {notFoundIds.length === 1 ? 'was' : 'were'} not assigned:{' '}
                {notFoundIds.join(', ')}
              </p>
            )}
            {skippedDept.length > 0 && (
              // FIN-13933 — the department rule stands; say which alerts it held back and why.
              <p data-testid="assign-skipped-department">
                {skippedDept.length} alert{skippedDept.length === 1 ? '' : 's'}{' '}
                {skippedDept.length === 1 ? 'was' : 'were'} not assigned because{' '}
                {selectedItemName || 'the selected investigator'} is not a member of
                the case&apos;s department:{' '}
                {skippedDept
                  .slice(0, SKIPPED_DEPT_SHOWN)
                  .map(
                    ({ alertId, caseId, departmentName }) =>
                      `${alertId} (case ${caseId}, ${departmentName})`
                  )
                  .join(', ')}
                {skippedDept.length > SKIPPED_DEPT_SHOWN &&
                  ` and ${skippedDept.length - SKIPPED_DEPT_SHOWN} more`}
              </p>
            )}
          </>
        ) : (
          <>
            <p>
              <b>Alerts Assigned Successfully</b>
            </p>
            {selectedAlertCount} Alert
            {selectedAlertCount > 1 ? 's' : ''}{' '}
            {selectedAlertCount === 1 ? 'has' : 'have'} been assigned to{' '}
            {selectedItemName}.
          </>
        )}
      </InfoModal>
      <InfoModal
        isOpen={visible}
        buttons={assignAlertsButtons}
        title="Assign an Investigator"
        showCheckIcon={false}
        width="65%">
        {investigatorsAreLoading ? (
          <StyledSpin size="large" />
        ) : (
          <Spin spinning={isLoading}>
            {hasError && <Alert type="error" message={errorMessage} />}
            {investigators.length > 0 ? (
              <>
                {/* FIN-12896 — count in the title + bounded scrolling tag cloud: with
                    hundreds of selected alerts the unbounded container pushed the
                    investigator picker and footer off-screen. */}
                <SelectedAlertContainerTitle>
                  {`Selected Alerts (${selectedAlertIds.length})`}
                </SelectedAlertContainerTitle>
                <SelectedAlertContainer
                  bounded
                  data-testid="assign-selected-alerts">
                  {renderSelectedIds()}
                </SelectedAlertContainer>
                <ChartSubTitleContainer>
                  <ChartSubTitleText>
                    Investigators and Assigned Cases & Alerts
                  </ChartSubTitleText>
                </ChartSubTitleContainer>
                <AnalystLoadWidget
                  axisLabel="Number of Cases & Alerts"
                  legendLabel=""
                  itemsPerPage={4}
                  isSelectable={true}
                  legend={legend}
                  items={items}
                  currentPage={chartCurrentPage}
                  selectedItemId={selectedItemId}
                  total={totalChartItems}
                  onPageChange={onChartPageChange}
                  onSelect={onSelect}
                />
                {unassignedOnlyWarning && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 16 }}
                    message="Only unassigned alerts within the selected customers will be assigned. Alerts already assigned to another investigator will remain unchanged."
                  />
                )}
              </>
            ) : (
              <Empty />
            )}
          </Spin>
        )}
      </InfoModal>
    </>
  );
};

const StyledSpin = styled(Spin)`
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

AssignAlertModal.propTypes = {
  handleExit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  selectedIds: PropTypes.array.isRequired,
  selectedAlerts: PropTypes.array.isRequired,
  investigators: PropTypes.arrayOf(InvestigatorItem),
  showInfoModal: PropTypes.bool,
  assignResult: PropTypes.shape({
    assigned: PropTypes.arrayOf(PropTypes.string),
    notFound: PropTypes.arrayOf(PropTypes.string),
    skippedDepartment: PropTypes.arrayOf(
      PropTypes.shape({
        alertId: PropTypes.string,
        caseId: PropTypes.number,
        departmentId: PropTypes.number,
        departmentName: PropTypes.string
      })
    )
  }),
  isLoading: PropTypes.bool,
  investigatorsAreLoading: PropTypes.bool,
  currentUser: UserType,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  unassignedOnlyWarning: PropTypes.bool,
  onAssignSuccess: PropTypes.func
};

const mapStateToProps = (state) => {
  // FIN-11360: defensive defaults so this connected modal can be mounted from
  // FRAML View even before those CM slices are populated. CM context always has
  // them, so behaviour there is unchanged.
  const { cmInvestigatorsWorkload = {}, cmAlertAction = {} } = state;
  const currentUser = getCurrentUser(state);

  return {
    investigators: cmInvestigatorsWorkload.list || [],
    showInfoModal: cmAlertAction.showInfoModal,
    assignResult: cmAlertAction.assignResult || null,
    investigatorsAreLoading: cmInvestigatorsWorkload.isLoading,
    isLoading: cmAlertAction.isLoading,
    currentUser,
    hasError: cmAlertAction.hasError,
    errorMessage: cmAlertAction.errorMessage
  };
};

export default connect(mapStateToProps)(AssignAlertModal);
